import { useState, useEffect } from "react";

import { useAdminStats, useAdminStatsTimeSeries } from "@/hooks/useAdminStats";
import {
  type StatsPeriod,
  type AdminStatsResponse,
  type AdminStatsDateRange,
  type StatsTimeSeriesInterval,
} from "@/services/admin/adminStatsService";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users,
  MessageSquare,
  ClipboardCheck,
  Gavel,
  Car,
  List,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { format, startOfMonth, endOfMonth } from "date-fns";
import type { Locale } from "date-fns";
import { sv, enUS } from "date-fns/locale";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Area,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import { cn } from "@/lib/utils";
import { LatestBidsPopup } from "./LatestBidsPopup";
import { Button } from "@/components/ui/button";
import { useNativePlatform } from "@/hooks/useNativePlatform";

const PERIODS_OVERVIEW: StatsPeriod[] = ["1d", "1w", "1m", "all", "custom"];
const PERIODS_TIME_SERIES: StatsPeriod[] = ["1w", "1m", "all", "custom"];
const INTERVALS: StatsTimeSeriesInterval[] = ["day", "week", "month"];

interface AdminStatsPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  period: StatsPeriod;
  onPeriodChange: (period: StatsPeriod) => void;
  initialStats?: AdminStatsResponse;
  /** When false, hide bidder identity / latest-bids detail (Mer). */
  canViewBidsOverview?: boolean;
}

function StatRow({
  label,
  value,
  sub,
}: {
  label: string;
  value: number | string;
  sub?: string;
}) {
  return (
    <div className="flex justify-between items-baseline gap-1.5 sm:gap-2 py-0.5 text-[10px] sm:text-xs">
      <span className="text-muted-foreground truncate">{label}</span>
      <span className="font-medium tabular-nums shrink-0 text-[10px] sm:text-xs">
        {value}
        {sub && (
          <span className="text-muted-foreground text-[9px] sm:text-[10px] ml-0.5">
            {sub}
          </span>
        )}
      </span>
    </div>
  );
}

type StatsLabels = {
  title?: string;
  period?: string;
  period1d?: string;
  period1w?: string;
  period1m?: string;
  periodAll?: string;
  periodCustom?: string;
  fromDate?: string;
  toDate?: string;
  applyPeriod?: string;
  changePeriod?: string;
  intervalDay?: string;
  intervalWeek?: string;
  intervalMonth?: string;
  loadingStats?: string;
  loadingTimeSeries?: string;
  noTimeData?: string;
  selectGrouping?: string;
  allTimeNote?: string;
  display?: string;
  lastYear12Months?: string;
  date?: string;
  users?: string;
  usersOnline?: string;
  usersNewInPeriod?: string;
  usersDeletedInPeriod?: string;
  leads?: string;
  leadsIncoming?: string;
  leadsFromGuests?: string;
  tests?: string;
  testsIncoming?: string;
  testsApproved?: string;
  testsRejected?: string;
  testsLedToListings?: string;
  testsPendingReview?: string;
  bids?: string;
  bidsCount?: string;
  bidsUniqueBidders?: string;
  bidsLatest?: string;
  bidsShowAll?: string;
  bidsNoBids?: string;
  showMore?: string;
  listings?: string;
  listingsNew?: string;
  listingsActive?: string;
  listingsDraft?: string;
  listingsEndingWithin24h?: string;
  listingsAuctionsWithBids?: string;
  listingsPurchasedDirect?: string;
  listingsPurchasedAuction?: string;
  listingsEnded?: string;
  listingsCancelled?: string;
  tabs?: { overview?: string; timeSeries?: string };
  latestBids?: Record<string, string>;
};

const PERIOD_LABEL_KEYS: Record<StatsPeriod, string> = {
  "1d": "period1d",
  "1w": "period1w",
  "1m": "period1m",
  all: "periodAll",
  custom: "periodCustom",
};

const INTERVAL_LABEL_KEYS: Record<StatsTimeSeriesInterval, string> = {
  day: "intervalDay",
  week: "intervalWeek",
  month: "intervalMonth",
};

function getDefaultCustomRange(): { from: Date; to: Date } {
  const now = new Date();
  const from = startOfMonth(now);
  const to = endOfMonth(now);
  return { from, to };
}

/** Format date range for display, e.g. "17 feb – 25 feb 2025" or "17 dec 2024 – 25 feb 2025" */
function formatDateRangeLabel(
  from: string,
  to: string,
  locale: Locale
): string {
  const dFrom = new Date(from + "T12:00:00");
  const dTo = new Date(to + "T12:00:00");
  const sameYear = dFrom.getFullYear() === dTo.getFullYear();
  const fromStr = format(dFrom, sameYear ? "d MMM" : "d MMM yyyy", { locale });
  const toStr = format(dTo, "d MMM yyyy", { locale });
  return `${fromStr} – ${toStr}`;
}

type AdminStatsCustomDateCalendarsProps = {
  calendarFrom: Date;
  calendarTo: Date;
  onCalendarFromChange: (date: Date) => void;
  onCalendarToChange: (date: Date) => void;
  dateLocale: Locale;
  fromLabel: string;
  toLabel: string;
};

/** Web: two calendars side by side. Native: one at a time with from/to toggle. */
function AdminStatsCustomDateCalendars({
  calendarFrom,
  calendarTo,
  onCalendarFromChange,
  onCalendarToChange,
  dateLocale,
  fromLabel,
  toLabel,
}: AdminStatsCustomDateCalendarsProps) {
  const { isNative } = useNativePlatform();

  const handleFromSelect = (d: Date | undefined) => {
    if (!d) return;
    onCalendarFromChange(d);
    if (d > calendarTo) onCalendarToChange(d);
  };

  const handleToSelect = (d: Date | undefined) => {
    if (!d) return;
    onCalendarToChange(d);
    if (d < calendarFrom) onCalendarFromChange(d);
  };

  if (isNative) {
    return (
      <div className="space-y-3 native-admin-stats-date-range">
        <div className="space-y-1.5">
          <Label className="text-[10px] sm:text-xs">{fromLabel}</Label>
          <DatePicker
            value={calendarFrom}
            onChange={(d) => d && handleFromSelect(d)}
            maxDate={calendarTo}
            locale={dateLocale}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] sm:text-xs">{toLabel}</Label>
          <DatePicker
            value={calendarTo}
            onChange={(d) => d && handleToSelect(d)}
            minDate={calendarFrom}
            locale={dateLocale}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <Label className="text-[10px] sm:text-xs">{fromLabel}</Label>
        <Calendar
          mode="single"
          selected={calendarFrom}
          onSelect={(d) => d && handleFromSelect(d)}
          locale={dateLocale}
          disabled={(d) => (calendarTo ? d > calendarTo : false)}
          className="rounded-md border"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-[10px] sm:text-xs">{toLabel}</Label>
        <Calendar
          mode="single"
          selected={calendarTo}
          onSelect={(d) => d && handleToSelect(d)}
          locale={dateLocale}
          disabled={(d) => (calendarFrom ? d < calendarFrom : false)}
          className="rounded-md border"
        />
      </div>
    </div>
  );
}

function OverviewTab({
  stats,
  period,
  onPeriodChange,
  onOpenLatestBids,
  canViewBidsOverview = false,
  dateRange,
  onDateRangeChange,
}: {
  stats: AdminStatsResponse | undefined;
  period: StatsPeriod;
  onPeriodChange: (p: StatsPeriod) => void;
  onOpenLatestBids?: () => void;
  canViewBidsOverview?: boolean;
  dateRange?: AdminStatsDateRange;
  onDateRangeChange?: (range: AdminStatsDateRange) => void;
}) {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const s = (adminLabels(language).stats ?? {}) as StatsLabels;
  const lb = s.latestBids ?? {};
  const locale = language === "en" ? "en-GB" : "sv-SE";
  const currency = lb.currency ?? "kr";
  const isDark = theme === "dark";
  const dateLocale = language === "en" ? enUS : sv;

  const defaultRange = getDefaultCustomRange();
  const [calendarFrom, setCalendarFrom] = useState<Date>(
    dateRange?.from ? new Date(dateRange.from + "T00:00:00") : defaultRange.from
  );
  const [calendarTo, setCalendarTo] = useState<Date>(
    dateRange?.to ? new Date(dateRange.to + "T00:00:00") : defaultRange.to
  );
  const [calendarExpanded, setCalendarExpanded] = useState(false);

  useEffect(() => {
    if (dateRange?.from) setCalendarFrom(new Date(dateRange.from + "T00:00:00"));
    if (dateRange?.to) setCalendarTo(new Date(dateRange.to + "T00:00:00"));
  }, [dateRange?.from, dateRange?.to]);

  const getPeriodLabel = (p: StatsPeriod) =>
    (s as Record<string, string>)[PERIOD_LABEL_KEYS[p]] ?? p;

  const handleApplyCustomRange = () => {
    const from = format(calendarFrom, "yyyy-MM-dd");
    const to = format(calendarTo, "yyyy-MM-dd");
    if (from <= to && onDateRangeChange) {
      onDateRangeChange({ from, to });
      setCalendarExpanded(false);
    }
  };

  useEffect(() => {
    if (period === "custom" && !dateRange) setCalendarExpanded(true);
  }, [period, dateRange]);

  if (!stats && period !== "custom") {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        {s.loadingStats ?? "Laddar statistik..."}
      </div>
    );
  }

  return (
    <div className="space-y-2.5 sm:space-y-4">
      <div className="flex flex-wrap items-center gap-1 sm:gap-2 pb-1 sm:pb-2">
        <span className="text-[10px] sm:text-xs text-muted-foreground">
          {s.period ?? "Period"}:
        </span>
        <div className="flex flex-wrap rounded-md sm:rounded-lg border border-border/60 overflow-hidden">
          {PERIODS_OVERVIEW.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPeriodChange(p)}
              className={cn(
                "px-1.5 py-0.5 sm:px-3 sm:py-1.5 text-[9px] sm:text-xs font-medium transition-colors",
                period === p
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-foreground",
              )}
            >
              {getPeriodLabel(p)}
            </button>
          ))}
        </div>
      </div>

      {period === "custom" && (
        <Collapsible open={calendarExpanded} onOpenChange={setCalendarExpanded}>
          <div className="rounded-md border border-border/60 bg-muted/10 overflow-hidden">
            {dateRange && (
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 p-3 text-left hover:bg-muted/20 transition-colors"
                >
                  <span className="text-[10px] sm:text-xs text-muted-foreground">
                    {s.period ?? "Period"}:
                  </span>
                  <span className="font-medium text-xs sm:text-sm tabular-nums">
                    {formatDateRangeLabel(dateRange.from, dateRange.to, dateLocale)}
                  </span>
                  <span className="text-[10px] sm:text-xs text-primary flex items-center gap-0.5">
                    {s.changePeriod ?? "Ändra period"}
                    {calendarExpanded ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                  </span>
                </button>
              </CollapsibleTrigger>
            )}
            <CollapsibleContent>
              <div className={cn("p-3 space-y-3", dateRange && "border-t border-border/60")}>
                <AdminStatsCustomDateCalendars
                  calendarFrom={calendarFrom}
                  calendarTo={calendarTo}
                  onCalendarFromChange={setCalendarFrom}
                  onCalendarToChange={setCalendarTo}
                  dateLocale={dateLocale}
                  fromLabel={s.fromDate ?? "Från datum"}
                  toLabel={s.toDate ?? "Till datum"}
                />
                <Button size="sm" onClick={handleApplyCustomRange} className="w-full sm:w-auto">
                  {s.applyPeriod ?? "Visa statistik"}
                </Button>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      {period === "custom" && !dateRange && (
        <p className="text-[10px] sm:text-xs text-muted-foreground">
          {s.period ?? "Period"}: {s.periodCustom ?? "Specifik period"} –{" "}
          {s.fromDate ?? "Från"}: {format(calendarFrom, "yyyy-MM-dd", { locale: dateLocale })} –{" "}
          {s.toDate ?? "Till"}: {format(calendarTo, "yyyy-MM-dd", { locale: dateLocale })}.{" "}
          {(s.applyPeriod ?? "Visa statistik").toLowerCase()}.
        </p>
      )}

      {!stats && period === "custom" && dateRange && (
        <div className="py-6 text-center text-muted-foreground text-sm">
          {s.loadingStats ?? "Laddar statistik..."}
        </div>
      )}

      {stats && (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-[repeat(3,minmax(0,8rem))_1fr_1fr] gap-1.5 sm:gap-3">
        <div
          className={cn(
            "rounded-md sm:rounded-lg border p-1.5 sm:p-2.5 min-w-0",
            isDark
              ? "bg-muted/20 border-border/50"
              : "bg-muted/10 border-border",
          )}
        >
          <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1.5 text-[10px] sm:text-xs font-medium">
            <Users className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 text-muted-foreground shrink-0" />
            {s.users ?? "Användare"}
          </div>
          <StatRow
            label={s.usersOnline ?? "Online"}
            value={stats.users.onlineCount}
          />
          <StatRow
            label={s.usersNewInPeriod ?? "Nyskapta"}
            value={stats.users.newInPeriod}
          />
          <StatRow
            label={s.usersDeletedInPeriod ?? "Raderade"}
            value={stats.users.deletedInPeriod}
          />
        </div>
        <div
          className={cn(
            "rounded-md sm:rounded-lg border p-1.5 sm:p-2.5 min-w-0",
            isDark
              ? "bg-muted/20 border-border/50"
              : "bg-muted/10 border-border",
          )}
        >
          <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1.5 text-[10px] sm:text-xs font-medium">
            <MessageSquare className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 text-muted-foreground shrink-0" />
            {s.leads ?? "Leads"}
          </div>
          <StatRow
            label={s.leadsIncoming ?? "Inkomna"}
            value={stats.leads.incomingInPeriod}
          />
          <StatRow
            label={s.leadsFromGuests ?? "Från gäster"}
            value={stats.leads.fromGuestsInPeriod}
          />
        </div>
        <div
          className={cn(
            "rounded-md sm:rounded-lg border p-1.5 sm:p-2.5 min-w-0",
            isDark
              ? "bg-muted/20 border-border/50"
              : "bg-muted/10 border-border",
          )}
        >
          <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1.5 text-[10px] sm:text-xs font-medium">
            <ClipboardCheck className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 text-muted-foreground shrink-0" />
            {s.tests ?? "Tester"}
          </div>
          <StatRow
            label={s.testsIncoming ?? "Inkomna"}
            value={stats.carTests.incomingInPeriod}
          />
          <StatRow
            label={s.testsApproved ?? "Godkända"}
            value={stats.carTests.approvedInPeriod}
          />
          <StatRow
            label={s.testsRejected ?? "Nekade"}
            value={stats.carTests.rejectedInPeriod}
          />
          <StatRow
            label={s.testsLedToListings ?? "Lett till annonser"}
            value={stats.carTests.ledToListingsInPeriod}
          />
          <StatRow
            label={s.testsPendingReview ?? "Väntande granskning"}
            value={stats.carTests.pendingReview}
          />
        </div>
        <div
          className={cn(
            "rounded-md sm:rounded-lg border p-1.5 sm:p-2.5 min-w-0",
            isDark
              ? "bg-muted/20 border-border/50"
              : "bg-muted/10 border-border",
          )}
        >
          <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1.5 text-[10px] sm:text-xs font-medium">
            <Gavel className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 text-muted-foreground shrink-0" />
            {s.bids ?? "Bud"}
          </div>
          <StatRow
            label={s.bidsCount ?? "Antal bud"}
            value={stats.bids.bidCountInPeriod}
          />
          <StatRow
            label={s.bidsUniqueBidders ?? "Unika budgivare"}
            value={stats.bids.uniqueBiddersInPeriod}
          />
          {canViewBidsOverview ? (
          <div className="mt-0.5 sm:mt-1.5 pt-0.5 sm:pt-1.5 border-t border-border/50">
            <div className="flex items-center justify-between gap-1 sm:gap-2">
              <span className="text-[9px] sm:text-[11px] text-muted-foreground">
                {s.bidsLatest ?? "Senaste"}
              </span>
              {onOpenLatestBids && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 sm:h-6 text-[9px] sm:text-[11px] px-1 sm:px-1.5 min-w-0"
                  onClick={onOpenLatestBids}
                  title={s.bidsShowAll ?? "Visa alla senaste bud"}
                >
                  <List className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5" />
                  {s.showMore ?? "Visa mer"}
                </Button>
              )}
            </div>
            {stats.bids.latestBids.length === 0 ? (
              <p className="text-[9px] sm:text-xs text-muted-foreground py-0.5">
                {s.bidsNoBids ?? "Inga bud"}
              </p>
            ) : (
              <ul className="text-[9px] sm:text-[11px] space-y-0.5 mt-0.5">
                {stats.bids.latestBids.slice(0, 5).map((b) => (
                  <li
                    key={b.id}
                    className="flex justify-between gap-1 sm:gap-2"
                  >
                    <span className="truncate min-w-0">
                      {b.listingTitle || "—"}
                    </span>
                    <span className="font-medium tabular-nums shrink-0 text-[9px] sm:text-xs">
                      {typeof b.amount === "number"
                        ? `${b.amount.toLocaleString(locale)} ${currency}`
                        : b.amount}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          ) : null}
        </div>
        <div
          className={cn(
            "rounded-md sm:rounded-lg border p-1.5 sm:p-2.5 min-w-0",
            isDark
              ? "bg-muted/20 border-border/50"
              : "bg-muted/10 border-border",
          )}
        >
          <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1.5 text-[10px] sm:text-xs font-medium">
            <Car className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 text-muted-foreground shrink-0" />
            {s.listings ?? "Annonser"}
          </div>
          <StatRow
            label={s.listingsNew ?? "Nya"}
            value={stats.listings.newInPeriod}
          />
          <StatRow
            label={s.listingsActive ?? "Aktiva"}
            value={stats.listings.active}
          />
          <StatRow
            label={s.listingsDraft ?? "Utkast"}
            value={stats.listings.draft}
          />
          <StatRow
            label={s.listingsEndingWithin24h ?? "Utgår inom 24 h"}
            value={stats.listings.endingWithin24h}
          />
          <StatRow
            label={s.listingsAuctionsWithBids ?? "Auktioner med bud"}
            value={stats.listings.auctionsWithBids}
          />
          <StatRow
            label={s.listingsPurchasedDirect ?? "Köpta direktköp"}
            value={stats.listings.purchasedDirectInPeriod}
          />
          <StatRow
            label={s.listingsPurchasedAuction ?? "Köpta auktioner"}
            value={stats.listings.purchasedAuctionInPeriod}
          />
          <StatRow
            label={s.listingsEnded ?? "Avslutade"}
            value={stats.listings.endedInPeriod}
          />
          <StatRow
            label={s.listingsCancelled ?? "Avbrutna"}
            value={stats.listings.cancelledInPeriod}
          />
        </div>
      </div>
      )}
    </div>
  );
}

function TimeSeriesChart({
  data,
  dataKey,
  name,
  color,
  noTimeDataLabel,
  dateLabel,
}: {
  data: { date: string; count: number }[];
  dataKey: string;
  name: string;
  color: string;
  noTimeDataLabel: string;
  dateLabel: string;
}) {
  const chartData = data.map((d) => ({
    ...d,
    [dataKey]: d.count,
    label: d.date.slice(5),
  }));

  if (chartData.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        {noTimeDataLabel}
      </p>
    );
  }

  return (
    <div className="h-[110px] sm:h-[220px] w-full">
      <ResponsiveContainer>
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.4} />
              <stop offset="95%" stopColor={color} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="label"
            tick={{ fontSize: 9 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
          <Tooltip
            formatter={(value: number) => [value, name]}
            labelFormatter={(label) => `${dateLabel}: ${label}`}
          />
          <Bar
            dataKey={dataKey}
            fill={color}
            radius={[4, 4, 0, 0]}
            barSize={20}
            opacity={0.7}
          />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            fill={`url(#grad-${dataKey})`}
            strokeWidth={2}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function TimeSeriesTab({
  period,
  onPeriodChange,
  dateRange,
  onDateRangeChange,
}: {
  period: StatsPeriod;
  onPeriodChange: (p: StatsPeriod) => void;
  dateRange?: AdminStatsDateRange;
  onDateRangeChange?: (range: AdminStatsDateRange) => void;
}) {
  const [interval, setTimeSeriesInterval] = useState<StatsTimeSeriesInterval>(
    period === "all" || period === "custom" ? "day" : "day",
  );
  const { language } = useLanguage();
  const s = (adminLabels(language).stats ?? {}) as StatsLabels;
  const dateLocale = language === "en" ? enUS : sv;
  const defaultRange = getDefaultCustomRange();
  const [calendarFrom, setCalendarFrom] = useState<Date>(
    dateRange?.from ? new Date(dateRange.from + "T00:00:00") : defaultRange.from
  );
  const [calendarTo, setCalendarTo] = useState<Date>(
    dateRange?.to ? new Date(dateRange.to + "T00:00:00") : defaultRange.to
  );
  const [calendarExpanded, setCalendarExpanded] = useState(false);

  useEffect(() => {
    if (dateRange?.from) setCalendarFrom(new Date(dateRange.from + "T00:00:00"));
    if (dateRange?.to) setCalendarTo(new Date(dateRange.to + "T00:00:00"));
  }, [dateRange?.from, dateRange?.to]);
  useEffect(() => {
    setTimeSeriesInterval(period === "all" ? "month" : "day");
  }, [period]);
  useEffect(() => {
    if (period === "1d") {
      onPeriodChange("1w");
    }
  }, [period, onPeriodChange]);

  const handleApplyCustomRange = () => {
    const from = format(calendarFrom, "yyyy-MM-dd");
    const to = format(calendarTo, "yyyy-MM-dd");
    if (from <= to && onDateRangeChange) {
      onDateRangeChange({ from, to });
      setCalendarExpanded(false);
    }
  };
  useEffect(() => {
    if (period === "custom" && !dateRange) setCalendarExpanded(true);
  }, [period, dateRange]);
  const effectivePeriod = period === "1d" ? "1w" : period;
  const timeSeriesEnabled = effectivePeriod !== "custom" || !!dateRange;
  const { timeSeries, isLoading } = useAdminStatsTimeSeries(
    effectivePeriod,
    interval,
    timeSeriesEnabled,
    effectivePeriod === "custom" ? dateRange : undefined,
  );
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const getPeriodLabel = (p: StatsPeriod) =>
    (s as Record<string, string>)[PERIOD_LABEL_KEYS[p]] ?? p;
  const getIntervalLabel = (i: StatsTimeSeriesInterval) =>
    (s as Record<string, string>)[INTERVAL_LABEL_KEYS[i]] ?? i;
  const intervalLabel = getIntervalLabel(interval).toLowerCase();
  const isAllTime = period === "all";
  const isCustomNoRange = period === "custom" && !dateRange;

  if (!isCustomNoRange && (isLoading || !timeSeries)) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        {s.loadingTimeSeries ?? "Laddar tidsdata..."}
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 pb-1.5 sm:pb-2">
        <span className="text-[10px] sm:text-xs text-muted-foreground">
          {s.period ?? "Period"}:
        </span>
        <div className="flex rounded-md sm:rounded-lg border border-border/60 overflow-hidden">
          {PERIODS_TIME_SERIES.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPeriodChange(p)}
              className={cn(
                "px-1.5 py-0.5 sm:px-3 sm:py-1.5 text-[9px] sm:text-xs font-medium transition-colors",
                effectivePeriod === p
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-foreground",
              )}
            >
              {getPeriodLabel(p)}
            </button>
          ))}
        </div>
      </div>

      {period === "custom" && (
        <Collapsible open={calendarExpanded} onOpenChange={setCalendarExpanded}>
          <div className="rounded-md border border-border/60 bg-muted/10 overflow-hidden">
            {dateRange && (
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 p-3 text-left hover:bg-muted/20 transition-colors"
                >
                  <span className="text-[10px] sm:text-xs text-muted-foreground">
                    {s.period ?? "Period"}:
                  </span>
                  <span className="font-medium text-xs sm:text-sm tabular-nums">
                    {formatDateRangeLabel(dateRange.from, dateRange.to, dateLocale)}
                  </span>
                  <span className="text-[10px] sm:text-xs text-primary flex items-center gap-0.5">
                    {s.changePeriod ?? "Ändra period"}
                    {calendarExpanded ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                  </span>
                </button>
              </CollapsibleTrigger>
            )}
            <CollapsibleContent>
              <div className={cn("p-3 space-y-3", dateRange && "border-t border-border/60")}>
                <AdminStatsCustomDateCalendars
                  calendarFrom={calendarFrom}
                  calendarTo={calendarTo}
                  onCalendarFromChange={setCalendarFrom}
                  onCalendarToChange={setCalendarTo}
                  dateLocale={dateLocale}
                  fromLabel={s.fromDate ?? "Från datum"}
                  toLabel={s.toDate ?? "Till datum"}
                />
                <Button size="sm" onClick={handleApplyCustomRange} className="w-full sm:w-auto">
                  {s.applyPeriod ?? "Visa statistik"}
                </Button>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      <p className="text-[10px] sm:text-xs text-muted-foreground">
        {s.selectGrouping ??
          "Välj hur data ska grupperas nedan. Graferna visar förändring över tid."}
        {isAllTime &&
          ` ${s.allTimeNote ?? 'Vid "Senaste året" visas de senaste 12 månaderna.'}`}
      </p>
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <span className="text-[10px] sm:text-xs text-muted-foreground">
          {s.display ?? "Visa"}:
        </span>
        <div className="flex rounded-md sm:rounded-lg border border-border/60 overflow-hidden">
          {INTERVALS.map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => setTimeSeriesInterval(i)}
              className={cn(
                "px-1.5 py-0.5 sm:px-3 sm:py-1.5 text-[9px] sm:text-xs font-medium transition-colors",
                interval === i
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-foreground",
              )}
            >
              {getIntervalLabel(i)}
            </button>
          ))}
        </div>
        {isAllTime && (
          <span className="text-[10px] sm:text-xs text-muted-foreground">
            {s.lastYear12Months ?? "(Senaste året = 12 månader)"}
          </span>
        )}
      </div>

      {isCustomNoRange ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          {s.fromDate ?? "Från datum"} – {s.toDate ?? "Till datum"}. {(s.applyPeriod ?? "Visa statistik").toLowerCase()}.
        </p>
      ) : (
      <div className="grid gap-2 sm:gap-6">
        <div>
          <h4 className="text-xs sm:text-sm font-medium mb-0.5 sm:mb-2">
            {s.leads ?? "Leads"} {intervalLabel}
          </h4>
          <TimeSeriesChart
            data={timeSeries.leads}
            dataKey="count"
            name={s.leads ?? "Leads"}
            color="#ea384c"
            noTimeDataLabel={s.noTimeData ?? "Ingen tidsdata för vald period."}
            dateLabel={s.date ?? "Datum"}
          />
        </div>
        <div>
          <h4 className="text-xs sm:text-sm font-medium mb-0.5 sm:mb-2">
            {s.tests ?? "Tester"} {intervalLabel}
          </h4>
          <TimeSeriesChart
            data={timeSeries.carTests}
            dataKey="count"
            name={s.tests ?? "Tester"}
            color="#22c55e"
            noTimeDataLabel={s.noTimeData ?? "Ingen tidsdata för vald period."}
            dateLabel={s.date ?? "Datum"}
          />
        </div>
        <div>
          <h4 className="text-xs sm:text-sm font-medium mb-0.5 sm:mb-2">
            {s.bids ?? "Bud"} {intervalLabel}
          </h4>
          <TimeSeriesChart
            data={timeSeries.bids}
            dataKey="count"
            name={s.bids ?? "Bud"}
            color="#388bea"
            noTimeDataLabel={s.noTimeData ?? "Ingen tidsdata för vald period."}
            dateLabel={s.date ?? "Datum"}
          />
        </div>
        <div>
          <h4 className="text-xs sm:text-sm font-medium mb-0.5 sm:mb-2">
            {s.listingsPurchasedDirect ?? "Köpta annonser"} {intervalLabel}
          </h4>
          <TimeSeriesChart
            data={timeSeries.listings}
            dataKey="count"
            name={s.listings ?? "Annonser"}
            color="#a855f7"
            noTimeDataLabel={s.noTimeData ?? "Ingen tidsdata för vald period."}
            dateLabel={s.date ?? "Datum"}
          />
        </div>
      </div>
      )}
    </div>
  );
}

export function AdminStatsPopup({
  open,
  onOpenChange,
  period,
  onPeriodChange,
  initialStats,
  canViewBidsOverview = false,
}: AdminStatsPopupProps) {
  const [latestBidsOpen, setLatestBidsOpen] = useState(false);
  const [dateRange, setDateRange] = useState<AdminStatsDateRange | undefined>(undefined);
  const statsEnabled = open && (period !== "custom" || !!dateRange);
  const { stats } = useAdminStats(
    period,
    statsEnabled,
    period === "custom" ? dateRange : undefined
  );
  const displayStats = stats ?? initialStats;
  const { language } = useLanguage();
  const s = (adminLabels(language).stats ?? {}) as StatsLabels;
  const tabs = s.tabs ?? { overview: "Översikt", timeSeries: "Över tid" };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-3xl max-h-[78vh] sm:max-h-[90vh] sm:w-full overflow-y-auto rounded-2xl p-3 sm:p-6">
          <Tabs defaultValue="overview" className="w-full">
            <DialogHeader className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
              <DialogTitle className="text-sm sm:text-lg">
                {s.title ?? "Adminstatistik"}
              </DialogTitle>
              <TabsList className="grid w-full grid-cols-2 sm:w-auto sm:inline-grid h-7 sm:h-8 text-[10px] sm:text-xs sm:mr-8">
                <TabsTrigger
                  value="overview"
                  className="text-[10px] sm:text-xs py-1 sm:py-1.5"
                >
                  {tabs.overview ?? "Översikt"}
                </TabsTrigger>
                <TabsTrigger
                  value="time-series"
                  className="text-[10px] sm:text-xs py-1 sm:py-1.5"
                >
                  {tabs.timeSeries ?? "Över tid"}
                </TabsTrigger>
              </TabsList>
            </DialogHeader>
            <TabsContent value="overview" className="mt-2.5 sm:mt-4">
              <OverviewTab
                stats={displayStats}
                period={period}
                onPeriodChange={onPeriodChange}
                onOpenLatestBids={
                  canViewBidsOverview
                    ? () => setLatestBidsOpen(true)
                    : undefined
                }
                canViewBidsOverview={canViewBidsOverview}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            </TabsContent>
            <TabsContent
              value="time-series"
              className="mt-2.5 sm:mt-4 pt-2 pb-8 sm:pt-0 sm:pb-4"
            >
              <TimeSeriesTab
                period={period}
                onPeriodChange={onPeriodChange}
                dateRange={period === "custom" ? dateRange : undefined}
                onDateRangeChange={setDateRange}
              />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
      {canViewBidsOverview ? (
        <LatestBidsPopup open={latestBidsOpen} onOpenChange={setLatestBidsOpen} />
      ) : null}
    </>
  );
}
